import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Reusable table-sorting primitives.
 *
 * Two variants share a `SortControl` shape so `SortableHead` works
 * with either:
 *
 *   * `useTableSort` — client-side, useful when the loader already
 *     returns the full result set (non-paginated views). The hook
 *     owns the state and returns the rows already `sorted` for the
 *     current selection.
 *   * `useUrlTableSort` — URL-driven, for server-paginated views.
 *     The route owns the `{ sort, dir }` search params, the loader
 *     ships them to the server, and this hook only computes the
 *     next pair on toggle. Sorting then applies to the whole result,
 *     not just the current page.
 */

export type SortValue = string | number | Date | null | undefined;

/** `key → accessor` map: each accessor pulls a comparable value off a row. */
export type SortAccessors<T> = Record<string, (row: T) => SortValue>;

export type SortDir = "asc" | "desc";

export type UseTableSort<T> = {
  /** Currently active column key, or `null` when unsorted. */
  sortKey: string | null;
  /** Active direction. Meaningless while `sortKey` is `null`. */
  sortDir: SortDir;
  /** 1st click on a key → asc, 2nd → desc, then back to asc. */
  toggle: (key: string) => void;
  /** The input rows ordered by the active column (stable). */
  sorted: T[];
};

/** Normalise a value for comparison; `Date` collapses to its epoch. */
function toComparable(value: SortValue): string | number | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value.getTime();
  return value;
}

const STORAGE_PREFIX = "hub:sort:";

type StoredSort = { key: string; dir: SortDir };

function readSort(storageKey: string): StoredSort | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_PREFIX + storageKey);
    const parsed = raw ? (JSON.parse(raw) as StoredSort) : null;
    if (
      parsed &&
      typeof parsed.key === "string" &&
      (parsed.dir === "asc" || parsed.dir === "desc")
    ) {
      return parsed;
    }
  } catch {
    // Blocked storage or a corrupt value: start unsorted.
  }
  return null;
}

function writeSort(storageKey: string, value: StoredSort): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(value));
  } catch {
    // localStorage disabled / full.
  }
}

/** Leaf route id (`/_app/customers/$id/`), so a detail page remembers one
 *  ordering for every entity rather than one per URL. */
export function useSortRouteId(): string {
  return useRouterState({ select: (s) => s.matches[s.matches.length - 1]?.routeId ?? "" });
}

export function useTableSort<T>(
  rows: T[],
  accessors: SortAccessors<T>,
  storageKey?: string,
): UseTableSort<T> {
  const [initial] = useState(() => (storageKey ? readSort(storageKey) : null));
  const [sortKey, setSortKey] = useState<string | null>(initial?.key ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(initial?.dir ?? "asc");

  useEffect(() => {
    if (storageKey && sortKey) writeSort(storageKey, { key: sortKey, dir: sortDir });
  }, [storageKey, sortKey, sortDir]);

  function toggle(key: string) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const accessor = accessors[sortKey];
    if (!accessor) return rows;
    // Decorate-sort-undecorate keeps the sort stable: ties fall back
    // to the original index.
    return rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const cmp = compareValues(accessor(a.row), accessor(b.row), sortDir);
        return cmp !== 0 ? cmp : a.index - b.index;
      })
      .map((entry) => entry.row);
  }, [rows, accessors, sortKey, sortDir]);

  return { sortKey, sortDir, toggle, sorted };
}

/**
 * Compares two cell values. Nullish values always sort last
 * regardless of direction; strings use `localeCompare`. Exported for
 * DataTable's tree-aware ordering, which can't use the flat `sorted`
 * output (sub-trees must stay under their parents).
 */
export function compareValues(a: SortValue, b: SortValue, dir: SortDir): number {
  const ca = toComparable(a);
  const cb = toComparable(b);
  if (ca === null && cb === null) return 0;
  if (ca === null) return 1;
  if (cb === null) return -1;

  let cmp: number;
  if (typeof ca === "string" && typeof cb === "string") {
    cmp = ca.localeCompare(cb);
  } else {
    cmp = ca < cb ? -1 : ca > cb ? 1 : 0;
  }
  return dir === "asc" ? cmp : -cmp;
}

/** The sort-state slice `SortableHead` needs — accepted from any
 *  `useTableSort<T>` or `useUrlTableSort` regardless of row type. */
type SortControl = Pick<UseTableSort<unknown>, "sortKey" | "sortDir" | "toggle">;

/**
 * URL-driven variant. The route holds the source of truth in its
 * search params (e.g. `?sort=name&dir=asc`); this hook just bridges
 * them to the `SortControl` interface `SortableHead` expects.
 *
 *   * The current values are passed in — the route reads them off
 *     `useSearch()` with its own typed schema.
 *   * `onChange(key, dir)` is what the route does on a header click;
 *     conventionally it pushes the new pair into the URL and resets
 *     `page` so the user lands back on page 1 of the new ordering.
 *   * 1st click on a key → asc, 2nd → desc, then back to asc — same
 *     toggle UX as the client-side hook.
 */
export function useUrlTableSort({
  sortKey,
  sortDir,
  onChange,
}: {
  sortKey: string | null;
  sortDir: SortDir;
  onChange: (key: string, dir: SortDir) => void;
}): SortControl {
  const routeId = useSortRouteId();
  const navigate = useNavigate();
  const restoredFor = useRef<string | null>(null);

  // An explicit `?sort=` wins; otherwise the remembered pair goes into the URL
  // (replace, so Back doesn't bounce through the unsorted state).
  // biome-ignore lint/correctness/useExhaustiveDependencies: restore once per route
  useEffect(() => {
    if (!routeId || restoredFor.current === routeId) return;
    restoredFor.current = routeId;
    if (sortKey) return;
    const stored = readSort(routeId);
    if (!stored) return;
    navigate({
      // @ts-expect-error generic search updater, the route's schema validates it.
      search: (prev: Record<string, unknown>) => ({ ...prev, sort: stored.key, dir: stored.dir }),
      replace: true,
    });
  }, [routeId]);

  useEffect(() => {
    if (routeId && restoredFor.current === routeId && sortKey) {
      writeSort(routeId, { key: sortKey, dir: sortDir });
    }
  }, [routeId, sortKey, sortDir]);

  return {
    sortKey,
    sortDir,
    toggle: (key: string) => {
      if (sortKey === key) {
        onChange(key, sortDir === "asc" ? "desc" : "asc");
      } else {
        onChange(key, "asc");
      }
    },
  };
}

type SortableHeadProps = {
  /** Column key — must match a key in the hook's accessor map. */
  sortKey: string;
  /** The hook instance driving this table. */
  sort: SortControl;
  /** Visible column label. */
  children: React.ReactNode;
  className?: string | undefined;
  /** Replaces the button's default `h-12 px-4` sizing — lets a dense
   *  table (DataTable `density="compact"`) keep its header height. */
  buttonClassName?: string | undefined;
};

/**
 * A `TableHead` with a clickable button that toggles sorting on its
 * column. Shows an up/down chevron for the active column and a faint
 * neutral chevron otherwise; mirrors the sort state into `aria-sort`.
 */
export function SortableHead({
  sortKey,
  sort,
  children,
  className,
  buttonClassName,
}: SortableHeadProps) {
  const active = sort.sortKey === sortKey;
  const ariaSort: React.AriaAttributes["aria-sort"] = active
    ? sort.sortDir === "asc"
      ? "ascending"
      : "descending"
    : "none";

  return (
    <TableHead className={cn("p-0", className)} aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => sort.toggle(sortKey)}
        className={cn(
          "flex w-full items-center gap-1 text-left font-medium hover:text-foreground",
          buttonClassName ?? "h-12 px-4",
        )}
      >
        <span>{children}</span>
        {active ? (
          sort.sortDir === "asc" ? (
            <ChevronUp className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          )
        ) : (
          <ChevronsUpDown
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40"
            aria-hidden="true"
          />
        )}
      </button>
    </TableHead>
  );
}
